// client/src/components/LoginScreen.jsx
// Anonymous-visitor landing page. Stack:
//   1. Two-pane hero — brand logo + Google sign-in on one side, brief
//      product intro + 6 feature tiles on the other.
//   2. "איך זה עובד" — 4 numbered steps walking through the operational
//      flow (register → credit → spend → report).
//   3. "מי משתמש במערכת" — 4 role cards explaining who touches what.
// Anyone who lands on the URL without an account should be able to read
// top-to-bottom and understand the system before deciding to ask for
// access. Signup itself is invite-only via Super Admin, by design.

import { useState, useEffect, Fragment } from 'react';
import VersionFooter from './VersionFooter.jsx';
import { startGoogleLogin } from '../hooks/useMe.js';
import {
  CreditCardIcon,
  ShoppingBagIcon,
  DevicePhoneMobileIcon,
  TrophyIcon,
  ChartBarIcon,
  ShieldCheckIcon,
  AcademicCapIcon,
  BanknotesIcon,
  BuildingLibraryIcon,
  Cog6ToothIcon,
  CalendarDaysIcon,
  TableCellsIcon,
} from './Icons.jsx';

const FEATURES = [
  {
    Icon: BuildingLibraryIcon,
    tone: 'joy',
    title: 'מותאם לציבור החרדי',
    description:
      'בנוי במיוחד לתלמודי תורה ולבתי יעקב — ללא שום צורך באינטרנט. הכל מבוסס על שוברים מודפסים, מסופונים, ועמדות של נדרים פלוס.',
  },
  {
    Icon: CreditCardIcon,
    tone: 'mint',
    title: 'שובר על מעשה טוב',
    description:
      'המורה רואה מעשה טוב — תפילה, סדר, עזרה לחבר — ונותן לתלמיד שובר נקודות מודפס. התלמיד טוען את הנקודות לכרטיס שלו בעצמו. בבתי ספר שיש בהם מכשיר סריקה נייד, המורה יכול גם לסרוק את הכרטיס ולזכות במקום.',
  },
  {
    Icon: ShoppingBagIcon,
    tone: 'gold',
    title: 'חנות הפרסים',
    description:
      'קיוסק קבוע או דוכן ליום מיוחד. התלמידים סורקים את כרטיסם וקונים פרסים בנקודות שצברו — בלי מזומן.',
  },
  {
    Icon: DevicePhoneMobileIcon,
    tone: 'brand',
    title: 'בירור יתרה בכל רגע',
    description:
      'התלמיד יכול לבדוק את היתרה שלו מתי שירצה — בעמדה בבית הספר, בעמדות נדרים פלוס, ובטלפון.',
  },
  {
    Icon: TrophyIcon,
    tone: 'clay',
    title: 'טבלת מובילים',
    description:
      'דירוג חי של כל התלמידים לפי יתרה, עם סינון לפי כיתה ומדליות לשלושת המקומות הראשונים.',
  },
  {
    Icon: ChartBarIcon,
    tone: 'sky',
    title: 'דוחות וייצוא לאקסל',
    description:
      'כל העסקאות בקובץ אחד, סיכום הפעילות, ופירוט כמה הוצא על כל פריט בחנות.',
  },
  {
    Icon: ShieldCheckIcon,
    tone: 'joy',
    title: 'תיעוד מלא',
    description:
      'כל זיכוי וחיוב נשמר עם המכשיר שרשם אותו, פירוט הפריטים וחותמת זמן. שום עסקה לא נמחקת לתמיד.',
  },
];

// Everything else the system does, so a first-time visitor gets the full
// picture (not just the 6 headline features). All real, shipped capabilities.
const CAPABILITIES = [
  {
    Icon: DevicePhoneMobileIcon,
    tone: 'brand',
    title: 'עמדת שירות עצמי (קיוסק)',
    description:
      'עמדת מסך של נדרים פלוס בבית הספר — מוגנת לחלוטין: ללא שום גישה לאינטרנט (גם לא לנדרים פלוס), אלא רק למערכת של בית הספר. מאובטחת ברמת החומרה, ברמת התוכנה, ובאמצעות סים מוגן של נדרים פלוס. התלמיד בודק לבד יתרה והיסטוריה ומממש שוברים, ואפשר לתזמן את שעות הפעילות — למשל רק בהפסקות.',
  },
  {
    Icon: CalendarDaysIcon,
    tone: 'mint',
    title: 'מודול נוכחות',
    description:
      'תלמיד שמעביר כרטיס בעמדת השירות העצמי בטווח השעות שתגדירו — מזוכה אוטומטית בנקודות, פעם אחת בכל יום.',
  },
  {
    Icon: TableCellsIcon,
    tone: 'gold',
    title: 'טעינת נקודות מאקסל',
    description:
      'זיכוי נקודות לתלמידים רבים בבת אחת מקובץ אקסל — לכל תלמיד הנקודות והסיבה שלו.',
  },
];

// Operational walk-through. Each step is a single concrete action — the
// reader should be able to mentally simulate it. Copy avoids generic
// marketing language ("transform", "engage") in favour of literal verbs
// ("סורק", "מוסיף", "בוחר").
const STEPS = [
  {
    n: 1,
    Icon: AcademicCapIcon,
    tone: 'brand',
    title: 'רושמים את הכיתות פעם אחת',
    body:
      'המנהל מוסיף את התלמידים — ידנית או בייבוא מקובץ אקסל. כל תלמיד מקבל כרטיס נדרים פלוס פעיל ושיוך לכיתה. אין מה לעדכן שוב בכל שנה — רק להוסיף את הכיתות החדשות.',
  },
  {
    n: 2,
    Icon: CreditCardIcon,
    tone: 'mint',
    title: 'המורה מזכה במהלך היום',
    body:
      'המורה רואה מעשה טוב — תפילה, סדר, עזרה לחבר — ונותן לתלמיד שובר נקודות מודפס. התלמיד טוען אותו לכרטיסו בעצמו, בלי טפסים, בלי מחברת, ובלי "אני אזכור אחר כך". ובמקום שיש מכשיר סריקה נייד, המורה יכול גם לסרוק את הכרטיס ולזכות במקום.',
  },
  {
    n: 3,
    Icon: ShoppingBagIcon,
    tone: 'gold',
    title: 'התלמיד פודה בחנות',
    body:
      'ביום היריד, קיוסק קבוע או יום הולדת — התלמיד בוחר מה שירצה מקטלוג הפרסים, סורק את הכרטיס, והנקודות יורדות מהיתרה. אם אין מספיק נקודות — המערכת לא תאשר. בלי מזומן, בלי לבלבל מי שילם על מה.',
  },
  {
    n: 4,
    Icon: ChartBarIcon,
    tone: 'sky',
    title: 'המערכת שומרת ומדווחת',
    body:
      'כל זיכוי וכל קנייה נשמרים עם המכשיר שרשם אותם, פירוט הפריטים וחותמת זמן. בלחיצה אחת אפשר להוריד את כל העסקאות לאקסל, לראות מי מוביל, ולהבין כמה הוצא על כל פריט בחנות.',
  },
];

const ROLES = [
  {
    Icon: AcademicCapIcon,
    tone: 'brand',
    title: 'התלמידים',
    summary: 'משתמשים בכרטיס, לא במערכת',
    body:
      'מקבלים כרטיס פעם אחת. סורקים אותו אצל המורה כדי לקבל נקודות, ובחנות כדי לקנות. אפשר לבדוק יתרה במסופון בכל רגע. אין חשבון, אין סיסמא, אין אפליקציה.',
  },
  {
    Icon: CreditCardIcon,
    tone: 'mint',
    title: 'המורים',
    summary: 'מחזיקים מסופון "מורה"',
    body:
      'נכנסים למסופון פעם בבוקר עם קוד בן 6 ספרות (לא צריך אימייל). רואים את קטלוג פעולות הזיכוי, סורקים תלמיד, לוחצים על הפעולה. שתי לחיצות לזיכוי.',
    device: 'מסופון EARN',
  },
  {
    Icon: BanknotesIcon,
    tone: 'sky',
    title: 'עמדת המכירה',
    summary: 'מחזיקים מסופון "חנות"',
    body:
      'אותו מסופון בדיוק, רק במצב SPEND. התלמיד סורק, המוכר לוחץ על הפריטים שלקח, רואה סכום סופי, מאשר. המערכת חוסמת אוטומטית אם אין מספיק יתרה.',
    device: 'מסופון SPEND',
  },
  {
    Icon: Cog6ToothIcon,
    tone: 'joy',
    title: 'המנהל',
    summary: 'נכנס לבק-אופיס דרך Google',
    body:
      'מקליק "הכנס עם Google" מכאן. מנהל מוסיף תלמידים, יוצר מכשירים (כל מכשיר מקבל קוד), עורך את קטלוג הפרסים והפעולות, מזמין עוד מנהלים, ומוריד דוחות.',
  },
];

// Tailwind needs the full class names present at build time for the JIT to
// emit them — that's why these maps are spelled out instead of being built
// with template strings inline in render.
const TONE_TILE = {
  mint:  'bg-mint-100  text-mint-700',
  gold:  'bg-gold-100  text-gold-700',
  brand: 'bg-brand-100 text-brand-700',
  clay:  'bg-clay-100  text-clay-700',
  sky:   'bg-sky-100   text-sky-700',
  joy:   'bg-joy-100   text-joy-700',
};
// Default display currency for the marketing page. Real camps use the
// camp's stored currency, but the public pricing is denominated in NIS.
const CURRENCY = '₪';

const TONE_NUM = {
  brand: 'bg-brand-gradient',
  mint:  'bg-mint-gradient',
  gold:  'bg-gold-gradient',
  sky:   'bg-sky-gradient',
  joy:   'bg-joy-gradient',
  clay:  'bg-gradient-to-br from-clay-400 to-joy-500',
};
const TONE_STRAP = {
  brand: 'bg-brand-gradient',
  mint:  'bg-mint-gradient',
  gold:  'bg-gold-gradient',
  sky:   'bg-sky-gradient',
  joy:   'bg-joy-gradient',
};

export default function LoginScreen() {
  return (
    <div className="min-h-screen flex flex-col">
      <main className="flex-1 px-4 py-8 sm:py-14 space-y-16 sm:space-y-24">

        {/* ═════════ Hero: card → live totals → sign-in ═════════
            One tight group so the card carries the brand, the totals sit
            quietly beneath it, and the single action (sign-in) closes it.
            A first-time visitor without a provisioned school signs in here
            too — App.jsx routes them to NoAccessScreen (→ /apply). */}
        <div className="space-y-6 sm:space-y-8">
          <CardShowcase />

          <LiveStats />

          <section className="w-full flex justify-center animate-fade-in">
            {/* Google's official white-button style: white pill, thin border,
                dark text, the multi-colour G mark. Kept neutral on purpose so
                the colourful card above stays the hero. Gentle lift on hover. */}
            <button
              type="button"
              onClick={() => startGoogleLogin(window.location.pathname)}
              className="inline-flex items-center justify-center gap-3 rounded-full
                         bg-white border border-ink-200 shadow-card
                         px-7 py-3.5 text-base font-bold text-ink-800
                         transition-all duration-150
                         hover:shadow-pop hover:-translate-y-0.5 hover:border-ink-300
                         active:translate-y-0 active:scale-[0.99]
                         focus-visible:outline-none focus-visible:ring-2
                         focus-visible:ring-brand-400 focus-visible:ring-offset-2"
            >
              <GoogleMark className="w-5 h-5 shrink-0" />
              כניסה עם Google
            </button>
          </section>
        </div>

        {/* Divider — a prominent separator between the sign-in action and the
            explanation below. `!my` overrides the page's large section rhythm so
            it sits close to both, not lost in a void. */}
        <div className="flex items-center justify-center gap-3 !my-8 sm:!my-10" aria-hidden="true">
          <span className="h-0.5 w-24 sm:w-40 rounded-full bg-gradient-to-l from-transparent to-brand-400" />
          <span className="w-2.5 h-2.5 rounded-full bg-brand-500 shadow-sm" />
          <span className="h-0.5 w-24 sm:w-40 rounded-full bg-gradient-to-r from-transparent to-brand-400" />
        </div>

        {/* ═════════ Intro + 6 features ═════════ */}
        <section className="w-full max-w-6xl mx-auto !mt-0">
          <div className="text-center mb-8 sm:mb-10 max-w-2xl mx-auto">
            <h2 className="h-page">
              שיהיה טוב לעשות{' '}
              <span className="bg-joy-gradient bg-clip-text text-transparent">טוב</span>
              .
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[...FEATURES, ...CAPABILITIES].map((f) => (
              <FeatureTile key={f.title} {...f} />
            ))}
          </div>
        </section>

        {/* ═════════ FLAGSHIP: VOUCHERS ═════════
            Placed before STEPS so the reader meets the differentiator
            early — right after the features list, before they've imagined
            the operational flow with terminals only. */}
        <VouchersFlagship />

        {/* ═════════ HOW IT WORKS ═════════ */}
        <section className="w-full max-w-6xl mx-auto">
          <div className="text-center mb-8 sm:mb-10">
            <p className="eyebrow">איך זה עובד</p>
            <h2 className="h-page mt-1.5">ארבעה צעדים, ואתם בפנים.</h2>
            <p className="mt-2 text-muted max-w-2xl mx-auto">
              המערכת בנויה סביב התהליך התפעולי האמיתי בבית הספר — לא טכנולוגיה
              בשביל טכנולוגיה. כל צעד נועד להחליף נייר אחד או מצוקה אחת בכיתה.
            </p>
          </div>

          <ol className="grid gap-4 sm:gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((step) => (
              <StepCard key={step.n} {...step} />
            ))}
          </ol>
        </section>

        {/* ═════════ ROLES ═════════ */}
        <section className="w-full max-w-6xl mx-auto">
          <div className="text-center mb-8 sm:mb-10">
            <p className="eyebrow">מי משתמש במערכת</p>
            <h2 className="h-page mt-1.5">ארבעה תפקידים, אפס בלבול.</h2>
            <p className="mt-2 text-muted max-w-2xl mx-auto">
              לכל תפקיד יש בדיוק את הכלי שמתאים לו — לא יותר. תלמיד לא רואה
              דוחות, מורה לא צריך להגדיר קטלוג, ומנהל לא צריך לסחוב מסופון
              כדי לחלק נקודות. ולמוסדות בלי מסופון לכל מורה —{' '}
              <strong className="text-ink-700">שובר הנקודות הוא שובר השיוויון</strong>:
              {' '}לא צריך להחזיק מכשיר ביד של כל מורה כדי שכולם יוכלו לזכות.
            </p>
          </div>

          <div className="grid gap-4 sm:gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {ROLES.map((role) => (
              <RoleCard key={role.title} {...role} />
            ))}
          </div>
        </section>

        {/* ═════════ PRICING ═════════ */}
        <section className="w-full max-w-6xl mx-auto">
          <div className="text-center mb-8 sm:mb-10">
            <p className="eyebrow">מחירון</p>
            <h2 className="h-page mt-1.5">פשוט ושקוף.</h2>
            <p className="mt-2 text-muted max-w-2xl mx-auto">
              התשלום מבוסס על מספר התלמידים. הקטלוג, המסופון הדיגיטלי, הדוחות,
              וכל ההגדרות — הכל כלול במחיר. ללא הקמה, ללא חוזים ארוכי טווח.
            </p>
          </div>

          <div className="grid gap-4 sm:gap-5 sm:grid-cols-3 items-stretch">
            <PricingCard
              tone="brand"
              Icon={BanknotesIcon}
              eyebrow="מנוי חודשי"
              price={`${CURRENCY}2`}
              unit="לתלמיד · לחודש"
              bullets={[
                <>תקרה של <strong className="text-ink-900">{CURRENCY}300 לחודש</strong> לבית ספר</>,
                'כל הפעולות במערכת כלולות',
                'ללא הקמה, ללא התחייבות',
              ]}
              featured
            />
            <PricingCard
              tone="gold"
              Icon={CreditCardIcon}
              eyebrow="הדפסת כרטיסים"
              price={`${CURRENCY}2`}
              unit="לכרטיס · חד פעמי"
              bullets={[
                'שם התלמיד מודפס על הכרטיס',
                'לוגו בית הספר כלול',
                'ללא תוספת תשלום',
              ]}
            />
            <PricingCard
              tone="mint"
              Icon={DevicePhoneMobileIcon}
              eyebrow="מסופונים"
              price="לפי דרישה"
              unit="קנייה או השכרה"
              bullets={[
                'פנו לנדרים פלוס לפרטים',
                <a key="m" href="mailto:office@nedar.im" dir="ltr" className="font-bold text-brand-700 hover:text-brand-900 underline">office@nedar.im</a>,
              ]}
            />
          </div>
        </section>

        {/* ═════════ Closing line — quiet contact reminder ═════════ */}
        {/* Previous version had a full CTA card here that duplicated three
            things already on the page: the "פנו לנדרים פלוס" note under
            the login button, the email on pricing card 3, and the visual
            card-pop pattern of the hero. Replaced with one centered line. */}
        <section className="w-full max-w-2xl mx-auto text-center">
          <p className="text-muted">
            רוצים להפעיל את TicTac בבית הספר? כתבו לנו ב־{' '}
            <a
              href="mailto:office@nedar.im"
              dir="ltr"
              className="font-bold text-brand-700 hover:text-brand-900 underline decoration-brand-300 decoration-2 underline-offset-4"
            >
              office@nedar.im
            </a>
          </p>
        </section>

      </main>
      <VersionFooter />
    </div>
  );
}

/* ───── Sub-components ───────────────────────────────────────── */

// Live totals across every connected school. Fetched from the public,
// 1h-cached /api/public/stats endpoint (no auth — counts only, no PII).
// A quiet single line under the card. The row is ALWAYS rendered so the
// layout is reserved up front — only the numbers fade in once they arrive,
// so there's no pop-in or page jump (and no flash of zeros).
function LiveStats() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    let alive = true;
    fetch('/api/public/stats', { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (alive && d) setStats(d); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const items = [
    { value: stats?.camps, label: 'בתי ספר' },
    { value: stats?.students, label: 'תלמידים' },
    { value: stats?.pointsDistributed, label: 'נקודות חולקו' },
  ];
  return (
    <div className="flex items-center justify-center gap-x-4 sm:gap-x-6 text-xs sm:text-sm text-ink-400">
      {items.map((s, i) => (
        <Fragment key={s.label}>
          {i > 0 && <span className="text-ink-200" aria-hidden>·</span>}
          <span className="inline-flex items-baseline gap-1.5">
            <span
              className={`num font-bold text-ink-600 tabular-nums transition-opacity duration-700 ${
                stats ? 'opacity-100' : 'opacity-0'
              }`}
            >
              {stats ? Number(s.value).toLocaleString('he-IL') : '0'}
            </span>
            <span>{s.label}</span>
          </span>
        </Fragment>
      ))}
    </div>
  );
}

// Hero eye-candy: the real printed TicTac card as a tilted, floating 3D
// mock-up. The two JPGs are the actual print samples (sharp-optimised into
// client/public), so it's a true product preview, not a redrawn approximation.
// Just the card — no surrounding copy. The card idly floats + glints (sheen)
// + has twinkling sparkles around it, but it does NOT rotate on its own:
// it flips front↔back only on click/tap, and clicking again flips it back.
function CardShowcase() {
  const [flipped, setFlipped] = useState(false);

  // Standard CR80 card ratio (85.6 × 54mm ≈ 1.586). Matches the artwork.
  const RATIO = '1.586 / 1';

  return (
    <section className="w-full max-w-md mx-auto animate-fade-in">
      <div className="relative px-4 py-6">
        {/* Soft brand glow + two drifting colour blobs behind the card, so it
            reads as floating over the confetti wash rather than pasted on. */}
        <div
          className="absolute inset-8 -z-10 rounded-[44px] blur-3xl opacity-70"
          style={{ background: 'radial-gradient(60% 60% at 50% 45%, rgba(99,102,241,0.30), transparent 70%)' }}
          aria-hidden="true"
        />
        <span className="blob-drift absolute -top-2 right-4 w-24 h-24 rounded-full bg-joy-300/45 blur-2xl -z-10" aria-hidden="true" />
        <span className="blob-drift-slow absolute -bottom-2 left-4 w-28 h-28 rounded-full bg-gold-300/45 blur-2xl -z-10" aria-hidden="true" />

        {/* Twinkling stars echoing the sparkles in the card art. */}
        <Sparkle className="twinkle text-gold-400 w-5 h-5 absolute -top-1 left-8"   style={{ animationDelay: '0s'   }} />
        <Sparkle className="twinkle text-joy-400  w-4 h-4 absolute top-7 -right-1"  style={{ animationDelay: '0.7s' }} />
        <Sparkle className="twinkle text-brand-400 w-3.5 h-3.5 absolute bottom-8 right-10" style={{ animationDelay: '1.4s' }} />
        <Sparkle className="twinkle text-mint-400 w-4 h-4 absolute -bottom-1 left-14" style={{ animationDelay: '2.1s' }} />

        <div className="card3d-float">
          <button
            type="button"
            onClick={() => setFlipped((f) => !f)}
            aria-label="הפוך את הכרטיס"
            className="card3d-scene block w-full bg-transparent border-0 p-0 cursor-pointer rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-4 focus-visible:ring-offset-transparent"
            style={{ aspectRatio: RATIO }}
          >
            <div className={`card3d w-full h-full ${flipped ? 'is-flipped' : ''}`}>
              <div className="card3d-face shadow-pop ring-1 ring-ink-900/5">
                <img src="/card-front.jpg" alt="כרטיס TicTac — צד קדמי" className="w-full h-full object-cover" loading="eager" draggable="false" />
                <span className="card-sheen" aria-hidden="true" />
              </div>
              <div className="card3d-face card3d-back shadow-pop ring-1 ring-ink-900/5">
                <img src="/card-back.jpg" alt="כרטיס TicTac — צד אחורי" className="w-full h-full object-cover" loading="lazy" draggable="false" />
                <span className="card-sheen" aria-hidden="true" />
              </div>
            </div>
          </button>
        </div>
      </div>
    </section>
  );
}

// Four-point sparkle glyph for the decorative twinkles around the card.
function Sparkle({ className = '', style }) {
  return (
    <svg className={className} style={style} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 0c.6 5.8 2.2 7.4 8 8-5.8.6-7.4 2.2-8 8-.6-5.8-2.2-7.4-8-8 5.8-.6 7.4-2.2 8-8z" />
    </svg>
  );
}

function VouchersFlagship() {
  return (
    <section className="w-full max-w-6xl mx-auto">
      <div className="text-center mb-8 sm:mb-10 max-w-2xl mx-auto">
        <p className="eyebrow">השובר המודפס</p>
        <h2 className="h-page mt-1.5">
          שובר נקודות.{' '}
          <span className="bg-gold-gradient bg-clip-text text-transparent">הפתרון המושלם.</span>
        </h2>
        <p className="mt-2 text-muted">
          לא לכל בית ספר יש תקציב למסופון אצל כל מורה. TicTac פותר את זה
          בלי לוותר על הפיצ'ר: המנהל מדפיס פנקסי שוברים בני 9 ספרות,
          המורה נותן שובר לתלמיד על מעשה טוב, והתלמיד טוען את הנקודות
          לכרטיסו לבד — בכל דרך שנוחה לו.
        </p>
      </div>

      <div className="card-pop relative overflow-hidden p-6 sm:p-10 lg:p-12">
        <div className="absolute inset-x-0 top-0 h-2 bg-gold-gradient" />

        <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-center">
          {/* Mock voucher — mirrors the real printed slip the manager
              generates: TicTac mark, "שובר נקודות" label, big amount,
              dashed code box with 9-digit code in 3-3-3 groups,
              redemption URL footer. */}
          <div className="order-2 lg:order-1">
            <MockVoucherCard />
          </div>

          {/* Right column: the three redemption channels. */}
          <div className="order-1 lg:order-2 space-y-4">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gold-100 text-gold-800 text-xs font-bold tracking-wide">
                <span className="w-1.5 h-1.5 rounded-full bg-gold-500" />
                שלוש דרכים להטענה
              </div>
              <p className="mt-3 text-ink-700 leading-relaxed">
                כל שובר ניתן לטעינה באחת משלוש הדרכים — לפי מה שנוח לתלמיד או למשפחה.
                המערכת מאמתת כרטיס + קוד ומזכה מיד.
              </p>
            </div>

            <ul className="space-y-3">
              <ChannelRow
                Icon={AcademicCapIcon}
                tone="brand"
                title="עמדה בבית הספר"
                detail="עמדת שירות עצמי בבית הספר — התלמיד מעביר כרטיס וטוען לבד"
              />
              <ChannelRow
                Icon={BuildingLibraryIcon}
                tone="mint"
                title="עמדת נדרים פלוס"
                detail="כל סניף או בית כנסת שבו יש עמדת נדרים פלוס פעילה"
              />
              <ChannelRow
                Icon={DevicePhoneMobileIcon}
                tone="sky"
                title="מערכת טלפונית"
                detail="חיוג מכל טלפון, כולל קווי בית — בלי אפליקציה, בלי סמארטפון"
              />
            </ul>

            <div className="pt-2 text-sm text-ink-600">
              גם בית ספר שאין לו תקציב למסופון לכל מורה — ואפילו לא עמדה
              בבית הספר — יכול להפעיל את TicTac במלואו. השובר המודפס{' '}
              <strong className="text-ink-900">מחליף את הצורך בכל מכשיר</strong>:
              {' '}המורה נותן שובר, והתלמיד טוען אותו בעמדת נדרים פלוס או בטלפון.
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function MockVoucherCard() {
  // Mirrors src/routes/vouchers.js renderCard() so the marketing preview
  // matches the actual printed slip. Colours are hard-coded (rose/gold/
  // indigo gradient strap, indigo amount + code-box) rather than going
  // through the brand palette, because the printed sheet is also
  // hard-coded to those exact hexes — the whole point is parity.
  return (
    <div className="relative mx-auto max-w-sm">
      <div className="absolute inset-0 translate-x-2 translate-y-2 rounded-[28px] bg-ink-200/40 -z-10" aria-hidden="true" />
      <div className="absolute inset-0 translate-x-1 translate-y-1 rounded-[28px] bg-ink-200/60 -z-10" aria-hidden="true" />

      <div className="bg-white rounded-[28px] border border-dashed border-ink-300 shadow-card overflow-hidden">
        {/* Rainbow strap — same gradient stops as the print's ::before bar */}
        <div
          className="h-2"
          style={{ background: 'linear-gradient(90deg, #f43f5e 0%, #fbbf24 50%, #4f46e5 100%)' }}
          aria-hidden="true"
        />

        <div className="p-6 sm:p-7">
          {/* Brand row: TicTac face icon + split-colour wordmark + camp line */}
          <div className="flex items-center gap-3 mb-4">
            <div
              className="w-10 h-10 rounded-xl grid place-items-center shrink-0"
              style={{ background: 'linear-gradient(135deg, #818cf8 0%, #4338ca 100%)' }}
              aria-hidden="true"
            >
              <svg viewBox="0 0 100 100" className="w-full h-full">
                <rect x="0" y="22" width="100" height="9" fill="#312e81" opacity="0.4" />
                <ellipse cx="36" cy="55" rx="6" ry="9" fill="#fff" />
                <ellipse cx="64" cy="55" rx="6" ry="9" fill="#fff" />
                <circle cx="38" cy="58" r="3" fill="#0f172a" />
                <circle cx="66" cy="58" r="3" fill="#0f172a" />
                <ellipse cx="22" cy="74" rx="9" ry="4" fill="#fb7185" opacity="0.65" />
                <ellipse cx="78" cy="74" rx="9" ry="4" fill="#fb7185" opacity="0.65" />
                <path d="M38 76 Q50 87 62 76" stroke="#fff" strokeWidth="4" fill="none" strokeLinecap="round" />
                <path
                  d="M50 6 L53 14 L62 14 L55 19 L57 28 L50 24 L43 28 L45 19 L38 14 L47 14 Z"
                  fill="#fbbf24" stroke="#fff" strokeWidth="1.5" strokeLinejoin="round"
                />
              </svg>
            </div>
            <div className="min-w-0">
              <div className="font-display font-extrabold leading-none text-lg" style={{ letterSpacing: '-0.02em' }}>
                <span style={{ color: '#f43f5e' }}>Tic</span>
                <span style={{ color: '#4f46e5' }}>Tac</span>
              </div>
              <div className="text-[11px] text-ink-500 font-semibold mt-1 truncate">בית ספר היסודי לדוגמה</div>
            </div>
          </div>

          {/* Amount label + big number */}
          <div className="text-center">
            <div className="text-[10px] font-bold uppercase text-ink-500" style={{ letterSpacing: '0.1em' }}>
              שובר נקודות
            </div>
            <div
              className="font-display font-extrabold leading-none mt-2 num"
              style={{ color: '#4338ca', fontSize: '56px', letterSpacing: '-0.04em' }}
            >
              <span className="opacity-85 me-1" style={{ fontSize: '32px' }}>⭐</span>10
            </div>
          </div>

          {/* Code box — lavender bg + indigo dashed border, matching print */}
          <div
            className="mt-5 rounded-xl text-center"
            style={{ border: '1.5px dashed #c7d2fe', background: '#eef2ff', padding: '10px 12px' }}
          >
            <div className="text-[10px] font-bold uppercase" style={{ color: '#4338ca', letterSpacing: '0.12em' }}>
              קוד שובר
            </div>
            <div
              className="font-display font-bold mt-1 num"
              dir="ltr"
              style={{ color: '#0f172a', fontSize: '26px', letterSpacing: '0.15em' }}
            >
              123 456 789
            </div>
          </div>

          {/* Footer — redemption URL, with a thin separator above */}
          <div className="mt-4 pt-3 flex items-center justify-between gap-2 border-t border-ink-100">
            <div className="min-w-0">
              <div className="text-[10px] text-ink-500 font-semibold leading-tight">למימוש:</div>
              <div className="font-bold text-xs mt-0.5" style={{ color: '#4338ca' }}>
                עמדת נדרים פלוס · טלפון
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const CHANNEL_TILE = {
  brand: 'bg-brand-100 text-brand-700',
  mint:  'bg-mint-100  text-mint-700',
  sky:   'bg-sky-100   text-sky-700',
};

function ChannelRow({ Icon, tone, title, detail }) {
  return (
    <li className="flex items-start gap-4 p-4 rounded-2xl bg-white border border-ink-100 hover:border-ink-200 transition-colors">
      <div className={`w-11 h-11 rounded-2xl grid place-items-center shrink-0 ${CHANNEL_TILE[tone]}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-bold text-ink-900">{title}</div>
        <div className="text-sm text-ink-600 leading-relaxed mt-0.5">{detail}</div>
      </div>
    </li>
  );
}

function FeatureTile({ Icon, tone, title, description }) {
  return (
    <div className="card p-5 text-start hover:shadow-pop hover:-translate-y-0.5 hover:border-brand-200 transition-all">
      <div className={`w-11 h-11 rounded-2xl grid place-items-center mb-3 ${TONE_TILE[tone]}`}>
        <Icon className="w-5 h-5" />
      </div>
      <h3 className="font-bold text-ink-900 mb-1.5">{title}</h3>
      <p className="text-sm text-ink-600 leading-relaxed">{description}</p>
    </div>
  );
}

function StepCard({ n, Icon, tone, title, body }) {
  return (
    <li className="card overflow-hidden hover:shadow-pop transition-shadow flex flex-col">
      <div className={`h-1.5 ${TONE_STRAP[tone]}`} />
      <div className="p-5 sm:p-6 flex-1">
        <div className="flex items-center gap-3 mb-3">
          {/* The numbered badge — gradient circle with the step number,
              so the section reads as a clear sequence at a glance. */}
          <span
            className={`w-10 h-10 rounded-2xl text-white grid place-items-center
                       font-display font-extrabold text-lg shadow-card num ${TONE_NUM[tone]}`}
          >
            {n}
          </span>
          <div className={`w-9 h-9 rounded-xl grid place-items-center ${TONE_TILE[tone]}`}>
            <Icon className="w-4.5 h-4.5" />
          </div>
        </div>
        <h3 className="font-bold text-ink-900 text-base mb-1.5 leading-snug">{title}</h3>
        <p className="text-sm text-ink-600 leading-relaxed">{body}</p>
      </div>
    </li>
  );
}

function PricingCard({ Icon, tone, eyebrow, price, unit, bullets, featured }) {
  // All three pricing cards share the SAME outer shape — same card class,
  // same padding, same radius, no translate offset — so they line up in
  // a clean grid row. The featured tier (monthly subscription) stands
  // out via a "מומלץ" mint badge in the eyebrow row, not via geometry,
  // which kept making it look bigger / misaligned. The brand-coloured
  // strap on every card is what carries the tone differentiation.
  return (
    <div className="card p-6 sm:p-7 relative overflow-hidden h-full flex flex-col hover:shadow-pop hover:-translate-y-0.5 transition-all">
      <div className={`absolute inset-x-0 top-0 h-1.5 ${TONE_STRAP[tone]}`} />
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-11 h-11 rounded-2xl grid place-items-center ${TONE_TILE[tone]}`}>
          <Icon className="w-5 h-5" />
        </div>
        <p className="eyebrow">{eyebrow}</p>
        {featured && <span className="badge-mint ms-auto">מומלץ</span>}
      </div>
      <div className="flex items-baseline gap-2">
        <span className="font-display font-extrabold text-4xl sm:text-5xl text-ink-900 num leading-none tracking-tight">
          {price}
        </span>
      </div>
      <div className="mt-2 text-sm font-semibold text-ink-600">{unit}</div>
      <ul className="mt-5 pt-5 border-t border-ink-100 space-y-2.5 text-sm text-ink-700 flex-1">
        {bullets.map((b, i) => (
          <li key={i} className="flex items-start gap-2">
            <CheckIcon className="w-4 h-4 mt-0.5 text-mint-600 shrink-0" />
            <span>{b}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function CheckIcon({ className = '' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
    </svg>
  );
}

function RoleCard({ Icon, tone, title, summary, body, device }) {
  return (
    <div className="card p-5 sm:p-6 hover:shadow-pop hover:-translate-y-0.5 transition-all">
      <div className={`w-12 h-12 rounded-2xl grid place-items-center mb-3 ${TONE_TILE[tone]}`}>
        <Icon className="w-6 h-6" />
      </div>
      <h3 className="font-bold text-ink-900 text-base">{title}</h3>
      <p className="text-xs font-semibold text-ink-500 mt-0.5">{summary}</p>
      {device && (
        <span className={`mt-2 inline-block ${tone === 'mint' ? 'badge-mint' : 'badge-sky'}`}>
          {device}
        </span>
      )}
      <p className="mt-3 text-sm text-ink-600 leading-relaxed">{body}</p>
    </div>
  );
}

// Google "G" mark — inline SVG so we don't pull a logo asset for one button.
function GoogleMark({ className = '' }) {
  return (
    <svg className={className} viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8a12 12 0 1 1 0-24c3 0 5.8 1.1 7.9 3l5.7-5.7A19.9 19.9 0 0 0 24 4a20 20 0 1 0 19.6 16.5z"/>
      <path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.6 16 19 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7A19.9 19.9 0 0 0 24 4a20 20 0 0 0-17.7 10.7z"/>
      <path fill="#4CAF50" d="M24 44a20 20 0 0 0 13.4-5.2l-6.2-5.2A12 12 0 0 1 12.7 28l-6.6 5.1A20 20 0 0 0 24 44z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3a12 12 0 0 1-4 5.6l6.2 5.2c-.4.4 6.5-4.8 6.5-14.8 0-1.3-.2-2.7-.4-3.5z"/>
    </svg>
  );
}
